/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { TouchableOpacity, View } from "react-native";

import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/types";
import Rodal from "rodal";
import "rodal/lib/rodal.css";
import Icon from "react-icons-kit";
import { closeRound } from "react-icons-kit/ionicons/closeRound";
import { useMutation } from "@apollo/client";
import { Redirect } from "react-router-dom";

import {
  DeactivateUserFromOrgResponse,
  DeactivateUserResponse,
} from "graphql/internal/generated";
import {
  DEACTIVATE_USER_FROM_ORG,
  DEACTIVATE_USER,
} from "graphql/internal/Mutations";
import { logoutResetState, instantiateMemberUser } from "config/ResetState";
import { callApi } from "config/UpduoService";
import { ActionCreators } from "actions";

import { Button, Text } from "UpduoLibrary";
import {
  skaleBlack,
  skaleLightRed,
  skalePrimaryBlue,
  skaleWhite,
} from "config/UpduoColors";

interface DeactivateModalProps {
  isVisible: boolean;
  onClose: Function;
  deactivate: boolean;
}

export default function Deactivate({
  isVisible,
  onClose,
  deactivate,
}: DeactivateModalProps) {
  const dispatch = useDispatch();
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const [shouldRedirectDash, setShouldRedirectDash] = useState(false);

  const [deactivateUserOrg, {}] = useMutation<DeactivateUserFromOrgResponse>(
    DEACTIVATE_USER_FROM_ORG,
    {
      onCompleted: () => {
        callApi("getPrimaryMemberList", undefined, (memberList) => {
          // memberList auto filters out any inactive orgs
          if (memberList.length < 1) {
            dispatch(ActionCreators.setAuthToken(""));
            //TODO refactor the SelfObject redux type to allow for an empty value
            //@ts-expect-error
            dispatch(ActionCreators.setSelfObject({}));
            window.location.reload();
          } else {
            callApi(
              "getPrimaryMemberToken",
              undefined,
              (token) => {
                dispatch(ActionCreators.setAuthToken(token["key"]));
                instantiateMemberUser(() => {
                  dispatch(ActionCreators.setPrimaryMemberList(memberList));
                  setShouldRedirectDash(true);
                });
              },
              undefined,
              memberList[0].id
            );
          }
        });
      },
      onError: (err) => {
        console.log("err: ", err);
      },
    }
  );
  const [deactivateUser] = useMutation<DeactivateUserResponse>(
    DEACTIVATE_USER,
    {
      onCompleted: () => {
        logoutResetState();
      },
    }
  );

  if (shouldRedirectDash) {
    return <Redirect to={"/"} />;
  }
  return (
    <Rodal
      visible={isVisible}
      onClose={() => onClose()}
      customStyles={{
        height: 300,
        width: 400,
        borderRadius: 12,
      }}
    >
      <View style={{ flex: 1 }}>
        <View
          style={{
            alignItems: "flex-end",
            justifyContent: "flex-end",
            marginRight: 10,
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <TouchableOpacity onPress={() => onClose()}>
            <Text
              style={{
                color: skaleBlack,
              }}
            >
              <Icon size={20} icon={closeRound} />
            </Text>
          </TouchableOpacity>
        </View>
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            isHeading={true}
            style={{
              fontSize: 20,
              color: skaleBlack,
              textAlign: "center",
            }}
          >
            {`Are you sure you want to ${
              deactivate ? "leave this community" : "delete your account"
            }?`}
          </Text>
          <View style={{ flexDirection: "column", marginTop: 35 }}>
            <Button
              onPress={() => {
                if (deactivate) {
                  deactivateUserOrg({
                    variables: {
                      deactivateUserFromOrgRequest: {
                        userId: selfObject.userId,
                      },
                    },
                  });
                } else {
                  deactivateUser();
                }
              }}
              color={skaleWhite}
              height={60}
              width={330}
              borderColor={skaleLightRed}
              borderWidth={2}
              textColor={skaleLightRed}
              text={deactivate ? "Leave" : "Delete"}
            />
            <View style={{ height: 10 }} />
            <Button
              onPress={() => {
                onClose();
              }}
              fontSize={16}
              height={60}
              color={skalePrimaryBlue}
              textColor={skaleWhite}
              width={330}
              text={"Cancel"}
            />
          </View>
        </View>
      </View>
    </Rodal>
  );
}
